import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  makeStyles,
  Paper,
} from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { useEffect, useState } from "react";
import { Control, useWatch } from "react-hook-form";

import { RecognitionApplicationViewModel } from "../../../domain/viewModels/RecognitionApplicationViewModel";
import { usersService } from "../../../services";

const useStyles = makeStyles((theme) => ({
  root: {
    border: `1px solid ${theme.palette.grey[300]}`,
    height: "9em",
    padding: 15,
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  leftContent: {},
  middleContent: {
    flexGrow: 1,
    padding: 20,
  },
  rightContent: {
    alignSelf: "flex-end",
  },
  avatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  label: {
    cursor: "pointer",
  },
}));

type Props = {
  label: string;
  upnFieldName: keyof Pick<
    RecognitionApplicationViewModel,
    "mainReviewerUPN" | "c1bReviewerUPN" | "dReviewerUPN"
  >;
  displayNameFieldName: keyof Pick<
    RecognitionApplicationViewModel,
    | "mainReviewerDisplayName"
    | "c1bReviewerDisplayName"
    | "dReviewerDisplayName"
  >;
  control: Control<RecognitionApplicationViewModel>;
  readOnly?: boolean;
  dataTest?: string;
};

export const ReviewerSectionAndDialog: React.FC<Props> = ({
  label,
  upnFieldName,
  displayNameFieldName,
  control,
  readOnly,
  dataTest,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // need to register ourselves as a control to be able to setValue
    // https://github.com/react-hook-form/react-hook-form/issues/1465
    control.register({ name: displayNameFieldName });
    control.register({ name: upnFieldName });
  }, [control, displayNameFieldName, upnFieldName]);

  const classes = useStyles();

  const vals = useWatch({
    control,
    name: [upnFieldName, displayNameFieldName],
  });

  const upn = vals[upnFieldName] || null;
  const displayName = vals[displayNameFieldName];

  return (
    <>
      <Paper
        id={`${upnFieldName}`}
        square
        className={classes.root}
        onClick={() => {
          !readOnly && setIsOpen(true);
        }}
      >
        <div className={classes.leftContent}>
          <Avatar className={classes.avatar} />
        </div>

        <div className={classes.middleContent}>
          <div>
            <label htmlFor={`${upnFieldName}`} className={classes.label}>
              <strong>{label}</strong>
            </label>
          </div>
          <div>{displayName || <em>Not specified</em>}</div>
        </div>
        {!readOnly ? (
          <div className={classes.rightContent}>
            <Button
              color="primary"
              data-test={dataTest}
              onClick={() => {
                setIsOpen(true);
              }}
            >
              Assign
            </Button>
          </div>
        ) : null}
      </Paper>
      {isOpen && (
        <UserDialog
          label={label}
          existingUpn={upn}
          onClose={() => setIsOpen(false)}
          onSelect={(upn, name) => {
            control.setValue(upnFieldName, upn, { shouldDirty: true });
            control.setValue(displayNameFieldName, name, { shouldDirty: true });
            setIsOpen(false);
          }}
        />
      )}
    </>
  );
};

type UserDialogProps = {
  label: string;
  existingUpn: string | null;
  onClose: () => void;
  onSelect: (upn: string, name: string) => void;
};

const UserDialog: React.FC<UserDialogProps> = ({
  label,
  onClose,
  onSelect,
  existingUpn,
}) => {
  const [userLookups] = useLookup({
    service: usersService,
    id: "upn",
    label: (item) => (item.fullName ? item.fullName : item.upn)
  });
  userLookups.sort((a, b) => (a.label > b.label ? 1 : -1));

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign {label}</DialogTitle>
      <Divider component="div" />
      <DialogContent>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Select
                label="Select user:"
                datatest="select-user-field"
                onChange={(selected) => {
                  onSelect(
                    selected.target.value,
                    String(
                      userLookups.find(
                        (item) => item.id === selected.target.value
                      )?.fullName
                    )
                  );
                  onClose();
                }}
                value={existingUpn}
              >
                {userLookups}
              </Select>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions></DialogActions>
    </Dialog>
  );
};
